import { useNavigate } from "react-router-dom";
import { ReportTable, type TableColumn } from "../ReportTable";
import { ReportSection } from "../ReportSection";
import { StatCard } from "../StatCard";
import { fmtPercent, progressColor, utilizationColor } from "./formatters";

interface ProjectHealthRow {
  id: string;
  code: string;
  name: string;
  status: string;
  progress: number;
  companyName: string | null;
  productName: string | null;
  startDate: string | null;
  endDate: string | null;
  taskCount: number;
  completedTasks: number;
  openTasks: number;
  overdueTasks: number;
  meetingCount: number;
  milestoneCount: number;
  completedMilestones: number;
  nextMilestone: { title: string; dueDate: string | null; status: string } | null;
  budgetTotal: number;
  budgetUsed: number;
  budgetUtilization: number;
}

interface ProjectHealthReportProps {
  data: ProjectHealthRow[];
}

function ProgressBar({ value }: { value: number }) {
  const color = progressColor(value);
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 w-[80px] overflow-hidden rounded-full"
        style={{ background: "hsl(210,16%,90%)" }}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="text-[12px] text-muted-foreground">{Math.round(pct)}%</span>
    </div>
  );
}

function UtilBar({ value }: { value: number }) {
  const color = utilizationColor(value);
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 w-[80px] overflow-hidden rounded-full"
        style={{ background: "hsl(210,16%,90%)" }}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="text-[12px]" style={{ color }}>
        {Math.round(value)}%
      </span>
    </div>
  );
}

export function ProjectHealthReport({ data }: ProjectHealthReportProps) {
  const navigate = useNavigate();
  const totalProjects = data.length;
  const active = data.filter((r) => r.status?.toLowerCase() === "active").length;
  const onTrack = data.filter((r) => r.progress >= 50).length;
  const atRisk = data.filter((r) => r.overdueTasks > 0).length;
  const completed = data.filter((r) => r.status?.toLowerCase() === "completed").length;

  const columns: Array<TableColumn<ProjectHealthRow>> = [
    {
      key: "name",
      label: "Project",
      render: (r) => (
        <div>
          <div className="font-medium text-foreground">{r.name}</div>
          <div className="font-mono text-[11px] text-muted-foreground">
            {r.code}
          </div>
        </div>
      ),
    },
    { key: "companyName", label: "Company", render: (r) => r.companyName ?? "—" },
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <span className="capitalize text-[12px] text-muted-foreground">
          {r.status?.toLowerCase().replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "progress",
      label: "Progress",
      render: (r) => <ProgressBar value={r.progress} />,
    },
    {
      key: "tasks",
      label: "Tasks",
      render: (r) => (
        <span className="text-[12px] text-muted-foreground">
          {r.completedTasks}/{r.taskCount}
        </span>
      ),
    },
    {
      key: "overdueTasks",
      label: "Overdue",
      render: (r) =>
        r.overdueTasks > 0 ? (
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ background: "hsl(0,84%,95%)", color: "hsl(0,72%,45%)" }}
          >
            {r.overdueTasks}
          </span>
        ) : (
          <span className="text-[12px] text-muted-foreground">0</span>
        ),
    },
    {
      key: "nextMilestone",
      label: "Next Milestone",
      render: (r) =>
        r.nextMilestone ? (
          <div>
            <div className="text-[12px] text-foreground">{r.nextMilestone.title}</div>
            {r.nextMilestone.dueDate && (
              <div className="text-[11px] text-muted-foreground">
                {new Date(r.nextMilestone.dueDate).toLocaleDateString("en-GB")}
              </div>
            )}
          </div>
        ) : (
          <span className="text-[12px] text-muted-foreground">—</span>
        ),
    },
    {
      key: "budgetUtilization",
      label: "Budget %",
      render: (r) =>
        r.budgetTotal > 0 ? (
          <UtilBar value={r.budgetUtilization} />
        ) : (
          <span className="text-[12px] text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total Projects" value={totalProjects} />
        <StatCard label="Active" value={active} tone="good" />
        <StatCard label="On Track" value={onTrack} hint="progress > 50%" />
        <StatCard label="At Risk" value={atRisk} tone="bad" hint="overdue tasks" />
        <StatCard label="Completed" value={completed} />
      </div>

      <ReportSection title="Projects">
        <ReportTable
          columns={columns}
          rows={data}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/projects/${r.id}`)}
          empty="No projects match the current filters."
        />
      </ReportSection>

      <div className="text-[11px] text-muted-foreground">
        Total projects: {fmtPercent(totalProjects > 0 ? (onTrack / totalProjects) * 100 : 0)} on track
      </div>
    </div>
  );
}
